import React, { useEffect, useRef, useState } from 'react';
import { FiChevronRight, FiMic, FiPlus, FiSidebar } from 'react-icons/fi';
import { FiLayers } from 'react-icons/fi';
import {
  deleteProject,
  getProject,
  listCustomRules,
} from '../utils/conversationApi';
import { CUSTOM_RULES_UPDATED_EVENT } from './CustomRulesPanel';
import ProjectPanel from './ProjectPanel';
import { recycleBinMessage, sharedDeleteMessage } from '../utils/projectSharing';
import './ProjectView.css';

// Persists the user's chosen panel width across visits/reloads -- see
// handleResizerMouseDown below. Bounds mirror ProjectPanel's own layout
// tolerance: narrow enough to still show the Instructions/Knowledge
// base/Project conversations tabs without wrapping, wide enough that it
// can't swallow the whole screen on a normal display (project-view-panel's
// own `max-width` in CSS is the final safety net for small viewports). 600
// is also exactly the width .project-panel-grid needs (minus this panel's
// own padding) to switch the Knowledge base/Conversations tabs to two
// columns -- see ProjectPanel.css's `@container (min-width: 560px)`.
const PANEL_WIDTH_STORAGE_KEY = 'kai_project_panel_width';
const PANEL_MIN_WIDTH = 280;
const PANEL_MAX_WIDTH = 600;

const getStoredPanelWidth = () => {
  try {
    const stored = parseInt(localStorage.getItem(PANEL_WIDTH_STORAGE_KEY), 10);
    if (Number.isFinite(stored)) {
      return Math.min(PANEL_MAX_WIDTH, Math.max(PANEL_MIN_WIDTH, stored));
    }
  } catch (e) {
    // Private browsing / storage disabled -- fall back to the default below.
  }
  return 360;
};

/**
 * A project's screen -- and the ONLY screen shown for anything inside a
 * project, landing page and live conversation alike. Modeled on Claude's own
 * project UI: a breadcrumb, the project's name, and a side panel
 * (ProjectPanel, variant="page") covering Instructions, Knowledge base, and
 * Project conversations.
 *
 * The middle is what changes, and only the middle: with no conversation open
 * it's a composer to start one; once one exists, App.js passes the live
 * <ChatInterface> in as `children` and it renders in the same slot. The
 * breadcrumb, title, and panel around it never unmount, so entering a
 * conversation doesn't swap the whole screen out for a generic-looking chat
 * (with its own competing title in the top-left) the way it used to -- the
 * project's identity is continuous from creation through every turn.
 */
function ProjectView({
  project,
  onProjectUpdated,
  onProjectDeleted,
  onOpenConversation,
  onStartConversationInProject,
  // Highlights the open conversation in the panel's Project conversations list.
  activeConversationId,
  // The active conversation's own name -- ChatInterface's own header hides
  // it while embedded in this shell (see its hideHeaderTitle prop), on the
  // assumption the Project conversations tab already shows it. That tab is
  // often closed, so the breadcrumb below is the one place left that always
  // shows which conversation is open.
  activeConversationName,
  // The active conversation's own Knowledge base restriction, for
  // ProjectPanel's Files tab checkboxes -- see .claude/plan/plan_knowledge_scope.md.
  activeConversationKnowledgeScope,
  // The scope picked (via those same checkboxes) for the conversation about
  // to be created, when there's no active one yet -- App.js owns this since
  // ChatInterface (a sibling of ProjectPanel here, both under this
  // component) also needs to read it at the first message's send time.
  pendingKnowledgeScope,
  onPendingKnowledgeScopeChange,
  onConversationDeleted,
  // Bumped by App whenever a conversation is created in or deleted from a
  // project (see refreshConversations) -- folded into panelRefreshTrigger.
  conversationsRefreshTrigger = 0,
  // Bumped by App each time a reply finishes -- passed through to
  // ProjectPanel, which re-reads its conversation list quietly.
  conversationActivityTrigger = 0,
  // True while the user has Conversations/Memory/Custom Rules/Logs open
  // (see App.js's anyRightDrawerOpen) -- that drawer takes priority over
  // this page's own Instructions/Knowledge base/Project conversations column, so it's
  // unmounted (not just visually hidden) rather than fighting the drawer
  // for the same screen real estate. The composer/title column simply grows
  // to fill the freed width since it's already flex: 1.
  hidePanel = false,
  // True while the top-bar's user menu is open (see App.js's
  // userDropdownOpen) -- blurs just this
  // page's own ProjectPanel column (Instructions/Knowledge base/History),
  // the same treatment the Conversations/Memory/Custom Rules/Logs drawers
  // already get from their own .dimmed-by-dropdown (App.css) when a dropdown
  // opens over them. The title/composer column on the left is deliberately
  // left alone -- only the sidebar-equivalent column blurs, same as those
  // other screens.
  dimmed = false,
  // Opens the top-bar Projects dropdown (the same one behind the toolbar's
  // own box icon) -- see the breadcrumb's own comment below for why this
  // link reuses that popup rather than a second, separate project picker.
  onOpenProjectsList,
  // Ref callback for the breadcrumb row's own slot div, which ChatInterface
  // portals its context meter/Handover/Share controls into (via App.js's
  // headerActionsPortalTarget) once this shell embeds it -- see that slot
  // div, just below, for why. Only meaningful once a conversation exists.
  headerActionsSlotRef,
  // Ref callback for the slot div right under the title, which ChatInterface
  // portals its model/message-count subtitle into (headerSubtitlePortalTarget).
  headerSubtitleSlotRef,
  // The focus mode the menu is on, or null in General Focus -- shown under
  // the title, since a project conversation runs in that mode.
  focusModeName = null,
  // The live <ChatInterface> for a conversation in THIS project, when there
  // is one (App.js decides -- see chatBelongsToViewingProject). Absent on the
  // landing page, where this component's own composer starts one instead.
  children,
}) {
  const [composerText, setComposerText] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  // Bumped only by handleNewConversationFromHistory (never by refreshTrigger's
  // other, unrelated causes -- a rename, a delete) so ProjectPanel can tell
  // "a fresh conversation was just started" apart from "the history list
  // changed for some other reason" and jump to its Knowledge base tab only
  // for the former. Passed down as the newConversationTrigger prop.
  const [newConversationTrigger, setNewConversationTrigger] = useState(0);
  // The user's whole rule library, only so this page's composer can show the
  // TITLES of the project's default_rule_ids as pills. Fetched once here (the
  // library is small) rather than threaded up out of ProjectPanel, which owns
  // its own copy for the checklist.
  const [ruleLibrary, setRuleLibrary] = useState([]);

  // --- Resizable panel (drag the handle on the panel's left edge) --------
  const [panelWidth, setPanelWidth] = useState(getStoredPanelWidth);
  const resizeStateRef = useRef(null);
  // Below ProjectView.css's 900px breakpoint the panel stacks full-width
  // under the composer instead of sitting beside it -- a dragged desktop
  // width has no meaning there, so the inline width (and the handle) are
  // skipped entirely and the stacked-layout CSS rule is left in charge.
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 900,
  );

  useEffect(() => {
    const handleWindowResize = () => setIsNarrowViewport(window.innerWidth <= 900);
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  // Narrow widths only: the panel is a drawer from the top of the content area
  // (under the app's top bar) to the bottom, with a narrow strip of dimmed page
  // left on its left. Opened by the toggle at the right end of the breadcrumb
  // row; closed by the X in its own header, a tap on that strip, or Escape. At
  // desktop widths it's the permanent column and none of this applies.
  const [panelDrawerOpen, setPanelDrawerOpen] = useState(false);
  const drawerOpen = isNarrowViewport && !hidePanel && panelDrawerOpen;

  // Closed again on a different project, when another right-hand drawer hides
  // the panel, or when the screen widens -- never reopens by itself.
  useEffect(() => {
    setPanelDrawerOpen(false);
  }, [project?.project_id, hidePanel, isNarrowViewport]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      // Escape also cancels the panel's rename/folder/header edits; leave
      // those to their own handlers instead of closing the drawer too.
      const tag = e.target && e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target && e.target.isContentEditable)) return;
      setPanelDrawerOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [drawerOpen]);

  const handleResizerMouseDown = (e) => {
    e.preventDefault();
    resizeStateRef.current = { startX: e.clientX, startWidth: panelWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      const resizeState = resizeStateRef.current;
      if (!resizeState) return;
      // The panel is the RIGHT column, so dragging the handle left (mouse
      // moving toward negative X) should grow it -- delta is inverted from
      // a left-column resize.
      const delta = resizeState.startX - e.clientX;
      const next = Math.min(
        PANEL_MAX_WIDTH, Math.max(PANEL_MIN_WIDTH, resizeState.startWidth + delta),
      );
      setPanelWidth(next);
    };
    const handleMouseUp = () => {
      if (!resizeStateRef.current) return;
      resizeStateRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setPanelWidth((current) => {
        try {
          localStorage.setItem(PANEL_WIDTH_STORAGE_KEY, String(current));
        } catch (e) {
          // Private browsing / storage disabled -- the width still applies
          // for the rest of this session, it just won't persist.
        }
        return current;
      });
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // What the panel's conversation list should re-fetch on. Its own trigger
  // covers actions taken here; conversationsRefreshTrigger covers the ones
  // App owns -- above all a conversation being CREATED, which happens after
  // the local trigger already fired (the composer bumps it before the
  // conversation exists) and so would otherwise never show up in the list
  // until the panel remounted.
  const panelRefreshTrigger = refreshTrigger + conversationsRefreshTrigger;

  // Refetched whenever the project's rule set changes, not just on mount: a
  // rule written from the panel's "+ Add instructions" and ticked on is new to
  // this list, and without the refetch its pill would be silently skipped for
  // having no title to show. Also on CUSTOM_RULES_UPDATED_EVENT, so renaming
  // an already-attached rule updates its pill's label -- same subscription
  // ChatInterface keeps for its own copy of the library. Deliberately NOT
  // keyed on panelRefreshTrigger: that fires when a conversation is created
  // or deleted, which cannot change any rule's title.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      listCustomRules()
        .then((rules) => { if (!cancelled) setRuleLibrary(rules); })
        .catch(() => {});
    };
    load();
    window.addEventListener(CUSTOM_RULES_UPDATED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(CUSTOM_RULES_UPDATED_EVENT, load);
    };
  }, [project?.default_rule_ids]);

  // A toast, not a fixture: fades on its own like ProjectPanel's "Applied"
  // confirmation, and is cleared immediately by retrying (see confirmDelete).
  useEffect(() => {
    if (!deleteError) return undefined;
    const timer = setTimeout(() => setDeleteError(null), 6000);
    return () => clearTimeout(timer);
  }, [deleteError]);

  if (!project) return null;

  const openDeleteModal = () => {
    setDeleteError(null);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteProject(project.project_id, { leave: project.shared_with > 0 });
      // Only close the modal / tell the parent the project is gone once the
      // server has actually confirmed it -- an optimistic close here would
      // show the project as deleted even if the request below fails.
      setDeleteModalOpen(false);
      onProjectDeleted?.(project.project_id);
    } catch (error) {
      // Left open (not dismissed) on failure -- the user's still looking
      // right at the Delete button to retry, and closing it would read as
      // "nevermind" when nothing about their request has changed.
      setDeleteError(error.message || 'Failed to delete project');
      if (error.status === 409) {
        // Someone joined or left since this dialog was shown: reload the
        // project so the dialog now says what Delete will really do.
        getProject(project.project_id)
          .then((fresh) => onProjectUpdated?.(fresh))
          .catch(() => {});
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmitComposer = (e) => {
    e.preventDefault();
    const text = composerText.trim();
    if (!text) return;
    onStartConversationInProject?.(project, text);
  };

  // Picking a conversation from the drawer is the user's answer, so slide it
  // away to show the chat; a no-op when it's a column (drawerOpen is false).
  const handleOpenConversation = (...args) => {
    if (drawerOpen) setPanelDrawerOpen(false);
    return onOpenConversation?.(...args);
  };

  const handleNewConversationFromHistory = (proj) => {
    if (drawerOpen) setPanelDrawerOpen(false);
    onStartConversationInProject?.(proj);
    setRefreshTrigger((n) => n + 1);
    setNewConversationTrigger((n) => n + 1);
  };

  // A live conversation in this project occupies the middle of the page
  // instead of the landing composer -- see the `children` prop.
  const hasChat = Boolean(children);

  // The project's standing instructions, as composer pills. Derived straight
  // from the `project` prop, so ticking one in the panel shows it here the
  // instant the save lands (App.js swaps in the updated project on
  // PROJECT_UPDATED_EVENT) -- the user shouldn't have to start a conversation
  // to find out whether their instruction took. Intersected with the library
  // rather than rendered from ids, so a rule deleted from the library leaves
  // no blank chip behind -- same reason ChatInterface derives its pills that way.
  const projectRulePills = (project?.default_rule_ids || [])
    .map((id) => ruleLibrary.find((r) => r.rule_id === id))
    .filter(Boolean);

  return (
    <div className="project-view">
      <div className={`project-view-main${hasChat ? ' project-view-main--chat' : ''}`}>
        {/* Identical on the landing page and mid-conversation -- same
            breadcrumb, same icon, same title size. Only its padding tightens
            once a chat is below it (see .project-view-main--chat). */}
        <div className="project-view-header">
          <div className="project-view-breadcrumb">
            {/* Opens the same Projects list/create popup the top-bar's own
                box icon opens -- clicking "which project am I in" is a
                request to see/switch projects, not (any more) a shortcut to
                start a new conversation in THIS one; the title right next
                to it (project-view-title-clickable) already covers that. */}
            <button
              type="button"
              className="project-view-breadcrumb-link"
              onClick={() => onOpenProjectsList?.()}
            >
              Projects
            </button>
            <FiChevronRight size={12} />
            <span>{project.name}</span>
            {/* ChatInterface portals its context meter/Handover/Share
                controls in here (see headerActionsPortalTarget) once it's
                embedded in this shell -- they used to render in its OWN
                header, far below this breadcrumb with a large empty gap
                where its title/subtitle would normally be, since that
                title is suppressed here (see hideHeaderTitle). Reuses
                ChatInterface.css's own .chat-header-right class for the
                exact same internal layout its portaled children already
                expect; project-view-header-actions here only adds the
                margin-left: auto that pushes it to this row's right edge. */}
            {hasChat && (
              <div className="project-view-header-actions chat-header-right" ref={headerActionsSlotRef} />
            )}
            {/* Opens/closes the project panel drawer. Only at the narrow
                widths where the panel is a drawer; the desktop column is
                always visible. */}
            {isNarrowViewport && !hidePanel && (
              <button
                type="button"
                className={`project-view-panel-toggle${drawerOpen ? ' project-view-panel-toggle--active' : ''}`}
                onClick={() => setPanelDrawerOpen((open) => !open)}
                title={drawerOpen ? 'Hide project panel' : 'Show project panel'}
                aria-label={drawerOpen ? 'Hide project panel' : 'Show project panel'}
                aria-expanded={drawerOpen}
              >
                <FiSidebar size={16} />
              </button>
            )}
          </div>

          <div className="project-view-title-row">
            {/* Same action as the breadcrumb link right above it (see
                handleNewConversationFromHistory) -- clicking the project's
                own name is just as reasonable a way to start a fresh
                conversation in it as clicking "Personal Projects" is. Shows
                the active conversation's own name instead of the project's,
                once one exists -- ChatInterface's own header hides that name
                while embedded in this shell (see its hideHeaderTitle prop),
                so this is the one place left it's still visible on screen.
                Renaming the project itself happens in ProjectPanel's own
                header now (see onDeleteProject passed to it below and
                handleStartRenameHeader in ProjectPanel.js), not here -- this
                title is purely a "start a new conversation" shortcut. */}
            {/* The icon sits outside the heading and spans both lines, so
                the name and the "Model • N messages" subtitle (portaled in
                by ChatInterface) left-align with each other beside it. */}
            <FiLayers className="project-view-title-icon" />
            <div className="project-view-title-stack">
              <h1
                className="project-view-title project-view-title-clickable"
                role="button"
                tabIndex={0}
                onClick={() => handleNewConversationFromHistory(project)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleNewConversationFromHistory(project);
                  }
                }}
                title="Start a new conversation in this project"
              >
                {hasChat && activeConversationName ? activeConversationName : project.name}
              </h1>
              {/* Under the title on the landing composer only. With the chat
                  embedded, its empty state shows the chip before the first
                  message, and the subtitle it portals in just below carries
                  it after. */}
              {focusModeName && !hasChat && (
                <span
                  className="chat-focus-mode-inline project-view-focus-mode"
                  title={`Focus mode: ${focusModeName}`}
                >
                  {focusModeName}
                </span>
              )}
              {hasChat && (
                <div className="project-view-subtitle" ref={headerSubtitleSlotRef} />
              )}
            </div>
          </div>
        </div>

        {hasChat ? (
          <div className="project-view-chat">{children}</div>
        ) : (
          <>
            {/* NOTE: in practice this branch essentially never renders --
                every way of opening a project (handleOpenProjectView,
                handleStartConversationInProject in App.js) sets
                pendingProjectForNextConversation, which makes App.js's
                chatBelongsToViewingProject true and embeds ChatInterface as
                `children` immediately, even before the first message. The
                pre-first-message Knowledge base source-selection control
                therefore lives in ChatInterface.js's own empty-chat welcome
                screen, not here -- see its getActiveProject() branch. */}
            {/* Reuses ChatInterface.css's actual composer classes (input-
                container/input-wrapper/input-section/message-input/send-button)
                verbatim -- not a look-alike -- so this is pixel-identical to the
                real composer, blue focus ring included, instead of drifting out
                of sync with it over time. Attach/rules only make sense once a
                conversation (and its file/rule attachments) actually exists, so
                they're present for visual consistency but disabled here. */}
            <form className="input-container project-view-composer" onSubmit={handleSubmitComposer}>
              <div className="input-wrapper">
                <div className="input-section">
                  <div className="input-actions">
                    <button
                      type="button"
                      className="attach-button plus-menu-button"
                      disabled
                      title="Attach files or custom rules once this conversation has started"
                    >
                      <FiPlus size={18} />
                    </button>
                  </div>
                  {/* Same pill chips the chat composer shows, in the same
                      place, styled by the same classes -- locked (no remove
                      button) because a project default is turned off in the
                      Instructions panel, not detached from one message. */}
                  {projectRulePills.map((rule) => (
                    <span
                      key={rule.rule_id}
                      className="custom-rule-pill custom-rule-pill-locked"
                      title={`${rule.text}\n\n(Always on in this project -- manage it under Instructions)`}
                    >
                      <span className="custom-rule-pill-title">{rule.title || rule.text}</span>
                    </span>
                  ))}
                  <textarea
                    className="message-input"
                    placeholder={isNarrowViewport ? 'Explore project…' :'Ask me anything about this project...'}
                    value={composerText}
                    rows={1}
                    onChange={(e) => setComposerText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmitComposer(e);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="mic-button"
                    disabled
                    title="Dictation is available once this conversation has started"
                  >
                    <FiMic size={16} />
                  </button>
                  <button
                    type="submit"
                    className="send-button"
                    disabled={!composerText.trim()}
                    title="Start conversation"
                    aria-label="Start conversation"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 11L12 6L17 11M12 18V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>
            </form>

            <p className="project-view-history-hint">
              Every conversation you start here is filed under this project.
              Browse past ones under Project conversations, on the right.
            </p>
          </>
        )}
      </div>

      {!hidePanel && isNarrowViewport && (
        <div
          className={`project-view-scrim${drawerOpen ? ' project-view-scrim--open' : ''}`}
          onClick={() => setPanelDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      {!hidePanel && (
        <div
          className={`project-view-panel${dimmed ? ' project-view-panel--dimmed' : ''}${
            isNarrowViewport ? ` project-view-panel--drawer${drawerOpen ? ' project-view-panel--open' : ''}` : ''
          }`}
          style={isNarrowViewport ? undefined : { width: panelWidth }}
          aria-hidden={isNarrowViewport && !drawerOpen ? true : undefined}
        >
          {!isNarrowViewport && (
            <div
              className="project-view-panel-resizer"
              onMouseDown={handleResizerMouseDown}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize project panel"
            />
          )}
          <ProjectPanel
            project={project}
            variant="page"
            activeConversationId={activeConversationId}
            activeConversationKnowledgeScope={activeConversationKnowledgeScope}
            pendingKnowledgeScope={pendingKnowledgeScope}
            onPendingKnowledgeScopeChange={onPendingKnowledgeScopeChange}
            onConversationDeleted={onConversationDeleted}
            onOpenConversation={handleOpenConversation}
            onClose={isNarrowViewport ? () => setPanelDrawerOpen(false) : undefined}
            onStartConversationInProject={handleNewConversationFromHistory}
            onProjectUpdated={onProjectUpdated}
            onProjectDeleted={onProjectDeleted}
            refreshTrigger={panelRefreshTrigger}
            conversationActivityTrigger={conversationActivityTrigger}
            newConversationTrigger={newConversationTrigger}
            onDeleteProject={openDeleteModal}
          />
        </div>
      )}

      {deleteModalOpen && (
        <div className="project-view-modal-overlay" onClick={() => !deleting && setDeleteModalOpen(false)} role="presentation">
          <div
            className="project-view-modal"
            role="dialog"
            aria-labelledby="project-view-delete-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            {project.shared_with > 0 ? (
              <>
                <h4 id="project-view-delete-title">Delete "{project.name}" for you?</h4>
                <p>{sharedDeleteMessage(project)}</p>
              </>
            ) : (
              <>
                <h4 id="project-view-delete-title">Move "{project.name}" to Recycle Bin?</h4>
                <p>{recycleBinMessage(project)}</p>
              </>
            )}
            <div className="project-view-modal-actions">
              <button
                type="button"
                className="project-view-modal-cancel"
                onClick={() => setDeleteModalOpen(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="project-view-modal-confirm"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {project.shared_with > 0
                  ? (deleting ? 'Leaving…' : 'Delete for me')
                  : (deleting ? 'Moving…' : 'Move to Recycle Bin')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteError && (
        <div className="project-view-delete-toast" role="alert">
          {deleteError}
        </div>
      )}
    </div>
  );
}

export default ProjectView;
